import {
  Typography,
  Card,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid as Grid2,
  Divider,
} from "@mui/material";
import { calculateAge, formatAge } from "../Utility/Utility";
import { useContext, useEffect, useState } from "react";
import { getPrescription } from "../API/PrescriptionApi";
import { useSnackbar } from "../Utility/UserMessageContext";
import AuthContext from "../Context/AuthProvider";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import { SelectableComorbidities } from "../Utility/Constants";

function GeneralPrescriptionReadOnlyView({ prescriptionId }) {
    const { auth } = useContext(AuthContext);
    const [visitDisplayId, setVisitDisplayId] = useState("");
    const [patientName, setPatientName] = useState("");
    const [patientMRN, setPatientMRN] = useState("");
    const [patientGender, setPatientGender] = useState("");
    const [patientDateOfBirth, setPatientDateOfBirth] = useState("");
    const [prescriptionDate, setPrescriptionDate] = useState("");
    const [complaint, setComplaint] = useState("");
    const [diagnosis, setDiagnosis] = useState("");
    const [comorbiditiesNotes, setComorbiditiesNotes] = useState("");
    const [medicalAndSurgicalHistory, setMedicalAndSurgicalHistory] = useState("");
    const [comorbiditiesConditions, setComorbiditiesConditions] = useState("");
    const [medicines, setMedicines] = useState([]);
    const [tests, setTests] = useState([]);
    const [additionalNotes, setAdditionalNotes] = useState([]);
    const [followupDate, setFollowupDate] = useState("");
    const showSnackbar = useContext(useSnackbar);

  useEffect(() => {
    const loadPrescription = async () => {
      const result = await getPrescription(auth, prescriptionId);
        
      if(result.isSuccess) {
        const prescription = result.data;
        // Assuming the prescription object has the same structure as the props
        setVisitDisplayId(prescription.visitDisplayId || "");
        setPatientName(prescription.patientName || "");
        setPatientMRN(prescription.patientMRN || "");
        setPatientGender(prescription.patientGender || "");
        setPatientDateOfBirth(prescription.patientDateOfBirth || "");
        setPrescriptionDate(prescription.createdOn || "");

        var detailedPrescriptionJson = JSON.parse(
                      prescription?.detailedPrescription
                    );
        if (prescription?.detailedPrescription) {
          setComplaint(detailedPrescriptionJson?.complaint);
          setDiagnosis(detailedPrescriptionJson?.diagnosis);
          setComorbiditiesConditions(detailedPrescriptionJson?.comorbidities);
          setComorbiditiesNotes(detailedPrescriptionJson?.comorbiditiesNotes);
          setMedicalAndSurgicalHistory(detailedPrescriptionJson?.medicalAndSurgicalHistory);
          setMedicines(detailedPrescriptionJson?.medicines);
          setTests(detailedPrescriptionJson?.tests);
          setAdditionalNotes(detailedPrescriptionJson?.additionalNotes);
          setFollowupDate(detailedPrescriptionJson?.followupDate);
        } else {
          setComplaint("");
          setDiagnosis("");
          setComorbiditiesConditions("");
          setComorbiditiesNotes("");
          setMedicalAndSurgicalHistory("");
          setMedicines([]);
          setTests([]);
          setAdditionalNotes("");
          setFollowupDate("");
        }
        // setComplaint(prescription.complaint);
        // setDiagnosis(prescription.diagnosis);
        // setComorbiditiesNotes(prescription.comorbiditiesNotes);
        // setMedicalAndSurgicalHistory(prescription.medicalAndSurgicalHistory);
        // setComorbiditiesConditions(prescription.comorbiditiesConditions);
        // setMedicines(prescription.medicines);
        // setTests(prescription.tests);
        // setAdditionalNotes(prescription.additionalNotes);
        // setFollowupDate(prescription.followupDate);
      }
      else {
        showSnackbar("Failed to load prescription: " + result.data.message, "error");
      }
    };
    loadPrescription();
  }, [prescriptionId]);
  
  
  const getSelectedItems = (items) => {
    if (!items) return "";
    return items
      .filter((item) => item.selected)
      .map((item) => item.displayValue)
      .join(", ");
  };

  return (
    <Grid2 container size={12} sx={{maxHeight: "80vh",  overflowY: "auto" }} >
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        boxShadow: 3,
        borderRadius: 3,
        p:1,
        background: " #f5f7fa",
      }}
    >
      {/* Patient & Visit Info */}
      <Box mb={3}>
        <Grid2 container>
          <Grid2 xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Patient Name
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {patientName}
            </Typography>
          </Grid2>
          <Grid2 xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Age & Gender
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {calculateAge(patientDateOfBirth)} &nbsp;{" "}
              {patientGender}
            </Typography>
          </Grid2>
          <Grid2 xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              MRN
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {patientMRN}
            </Typography>
          </Grid2>
        </Grid2>
      </Box>

      <Box mb={3}>
        <Grid2 container>
          <Grid2 xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Visit ID
            </Typography>
            <Typography variant="body1">
              {visitDisplayId || "-"}
            </Typography>
          </Grid2>
          <Grid2 xs={12} md={4}></Grid2>
          <Grid2 xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Date & Time
            </Typography>
            <Typography variant="body1">
              {convertUTCToTimeZoneDateTimeString(prescriptionDate, auth.facilityTimeZone) || "-"}
            </Typography>
          </Grid2>
        </Grid2>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Complaint */}
      <Box mb={2}>
        <Typography fontWeight="bold" gutterBottom>
          Chief Complaints
        </Typography>
        <Typography sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
          {complaint || "No complaint recorded"}
        </Typography>
      </Box>

      {/* Comorbidities */}
      <Box mb={2}>
        <Typography fontWeight="bold" gutterBottom>
          Comorbidities
        </Typography>
        <Typography sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
          {getSelectedItems(comorbiditiesConditions) ||
            "No comorbidities recorded"}
        </Typography>
      </Box>

      {/* Comorbidities Notes */}
      <Box mb={2}>
        <Typography fontWeight="bold" gutterBottom>
          Comorbidities Notes
        </Typography>
        <Typography sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
          {comorbiditiesNotes || "No comorbidities notes recorded"}
        </Typography>
      </Box>

      {/* Medical & Surgical History */}
      <Box mb={2}>
        <Typography fontWeight="bold" gutterBottom>
          Medical \ Surgical History
        </Typography>
        <Typography sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
          {medicalAndSurgicalHistory ||
            "No medical and surgical history recorded"}
        </Typography>
      </Box>

      {/* Diagnosis */}
      <Box mb={2}>
        <Typography fontWeight="bold" gutterBottom>
          Diagnosis Notes
        </Typography>
        <Typography sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
          {diagnosis || "No diagnosis recorded"}
        </Typography>
      </Box>

      {/* Medicines */}
      <Box mb={2}>
        <Typography fontWeight="bold" gutterBottom>
          Medicines
        </Typography>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            boxShadow: 1,
            p: 2,
            background: "#fff",
            mb: 2,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  background:
                    "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)",
                }}
              >
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", width: 40 }}
                >
                  #
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Medicine</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Frequency</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Instructions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {medicines && medicines.length > 0 ? (
                medicines.map((med) => (
                  <TableRow key={med.serialNo} hover>
                    <TableCell align="center">{med.serialNo}</TableCell>
                    <TableCell>{med.name || "-"}</TableCell>
                    <TableCell>{med.duration || "-"}</TableCell>
                    <TableCell>{med.frequency || "-"}</TableCell>
                    <TableCell>{med.instructions || "-"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No medicines prescribed
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </Box>

      {/* Recommended Tests */}
      <Box mb={2}>
        <Typography fontWeight="bold" gutterBottom>
          Recommended Tests
        </Typography>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            boxShadow: 1,
            p: 2,
            background: "#fff",
            mb: 2,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  background:
                    "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)",
                }}
              >
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", width: 40 }}
                >
                  #
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Test Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Tentative Date of Test
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Instructions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tests && tests.length > 0 ? (
                tests.map((test) => (
                  <TableRow key={test.serialNo} hover>
                    <TableCell align="center">{test.serialNo}</TableCell>
                    <TableCell>{test.name || "-"}</TableCell>
                    <TableCell>{test.toBeDoneOn || "-"}</TableCell>
                    <TableCell>{test.instructions || "-"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No tests recommended
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </Box>

      {/* Additional Notes */}
      <Box mb={2}>
        <Typography fontWeight="bold" gutterBottom>
          Additional Notes
        </Typography>
        <Typography sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
          {additionalNotes || "No additional notes provided"}
        </Typography>
      </Box>

      {/* Follow Up Date */}
      <Box mb={2}>
        <Typography fontWeight="bold" gutterBottom>
          Follow Up Date
        </Typography>
        <Typography sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
          {followupDate || "No follow-up date scheduled"}
        </Typography>
      </Box>
    </Card>
    </Grid2>
  );
}

export default GeneralPrescriptionReadOnlyView;
